import type { WeddingFeatures } from '@bridebook/toolbox/src/recommendations/wedding-features';
import { z } from 'zod/v4';
import { SupplierTypeEnum } from '../../supplier-types';

// The couple's search state for this request, built in the browser: where and what they
// search, the results page's ranking inputs and its rollout settings.

// Request size caps. The browser trims to them, so a long history never fails the request.
export const SEARCH_AREA_MAX_CHARS = 100;
export const SEARCH_INTERACTIONS_MAX = 500;

export const searchContextSchema = z.object({
  // Whether the couple is on the results page. A question there is about what is on screen,
  // and only then are the page's ranking inputs posted.
  onPage: z.boolean().optional(),
  // The search page's area: their last pick, else their wedding location. Null for a
  // country-wide search.
  area: z.string().max(SEARCH_AREA_MAX_CHARS).nullish(),
  // The supplier category the search is set to.
  type: SupplierTypeEnum.nullish(),
  // The results page's ranking inputs. Present only on that page.
  personalisation: z
    .object({
      interactions: z.array(z.string().max(100)).max(SEARCH_INTERACTIONS_MAX).optional(),
      interactionActions: z.array(z.string().max(100)).max(SEARCH_INTERACTIONS_MAX).optional(),
      weddingFeatures: z.custom<WeddingFeatures>().optional(),
      weddingBudget: z.number().optional(),
    })
    .optional(),
  // The settings the couple's own results page runs under.
  tuning: z.object({ appendIncompleteSearch: z.boolean() }).optional(),
});

export type SearchContext = z.infer<typeof searchContextSchema>;
export type SearchPersonalisation = NonNullable<SearchContext['personalisation']>;

/**
 * Where this turn searches when the couple names no place. On the results page, the page's
 * area wins, even when country-wide. Elsewhere, the page's last pick wins, else the saved
 * search area.
 */
export const resolveSearchArea = (
  search: Pick<SearchContext, 'onPage' | 'area'> | undefined,
  profile: { searchArea?: string | null } | null | undefined,
): string | null => {
  const picked = search?.area?.trim();
  if (picked) {
    return picked;
  }
  if (search?.onPage) {
    return null;
  }
  return profile?.searchArea?.trim() || null;
};
